import { EXPLORE_TABS, type ExploreCard, type ExploreTab } from '@mcgames/contracts';
import { Badge, Button, CoverTile, Spinner, Tabs, TextInput } from '@mcgames/ui';
import { type FormEvent, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { HttpError } from '../../api/http.ts';
import { ErrorText } from '../games/ErrorText.tsx';
import adminStyles from '../Admin.module.css';
import { type ExploreSource, useExplore, useExploreImport } from './explore-hooks.ts';
import styles from './Explore.module.css';

const STORE_NAMES = { steam: 'Steam', gog: 'GOG' } as const;

function isTab(value: string | null): value is ExploreTab {
  return (EXPLORE_TABS as readonly string[]).includes(value ?? '');
}

const cardKey = (card: Pick<ExploreCard, 'source' | 'storeId'>) => `${card.source}:${card.storeId}`;

/**
 * Store lists (Steam top sellers and new releases, GOG classics) and a search across both, as a
 * grid of covers; "Import" adds a game to the catalog as a draft.
 */
export function ExplorePage() {
  const { t } = useTranslation('admin');
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab: ExploreTab = isTab(tabParam) ? tabParam : 'popular';
  const query = (params.get('q') ?? '').trim();
  const source: ExploreSource = query !== '' ? { kind: 'search', q: query } : { kind: 'tab', tab };
  const [text, setText] = useState(query);
  const idPrefix = useId();
  // Games imported on this page, by card key; they show "In catalog" without a reload.
  const [imported, setImported] = useState<Record<string, string>>({});

  useEffect(() => {
    setText(query);
  }, [query]);

  const selectTab = (key: string) => {
    if (isTab(key)) setParams({ tab: key });
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const q = text.trim();
    setParams(q === '' ? { tab } : { tab, q });
  };

  const markImported = (card: ExploreCard, gameId: string) =>
    setImported((current) => ({ ...current, [cardKey(card)]: gameId }));

  return (
    <div className={adminStyles['page']}>
      <div className={styles['intro']}>
        <h1 className={adminStyles['heading']}>{t('explore.title')}</h1>
        <p className={adminStyles['muted']}>{t('explore.intro')}</p>
      </div>
      <form onSubmit={submit} role="search" className={styles['searchForm']}>
        <div className={styles['searchField']}>
          <TextInput
            label={t('explore.search.label')}
            placeholder={t('explore.search.placeholder')}
            value={text}
            maxLength={200}
            onChange={(event) => setText(event.target.value)}
          />
        </div>
        <Button type="submit">{t('explore.search.submit')}</Button>
        {query !== '' && (
          <Button variant="ghost" onClick={() => setParams({ tab })}>
            {t('explore.search.clear')}
          </Button>
        )}
      </form>
      {query === '' ? (
        <>
          <Tabs
            idPrefix={idPrefix}
            items={EXPLORE_TABS.map((key) => ({ key, label: t(`explore.tabs.${key}`) }))}
            value={tab}
            onChange={selectTab}
          />
          <section
            role="tabpanel"
            id={`${idPrefix}-panel-${tab}`}
            aria-labelledby={`${idPrefix}-tab-${tab}`}
            className={styles['panel']}
          >
            <p className={adminStyles['muted']}>{t(`explore.tabHints.${tab}`)}</p>
            <ExploreResults
              key={tab}
              source={source}
              imported={imported}
              onImported={markImported}
            />
          </section>
        </>
      ) : (
        <section aria-label={t('explore.search.results', { query })} className={styles['panel']}>
          <h2 className={adminStyles['subheading']}>{t('explore.search.results', { query })}</h2>
          <ExploreResults
            key={query}
            source={source}
            imported={imported}
            onImported={markImported}
          />
        </section>
      )}
    </div>
  );
}

function ExploreResults({
  source,
  imported,
  onImported,
}: {
  source: ExploreSource;
  imported: Record<string, string>;
  onImported: (card: ExploreCard, gameId: string) => void;
}) {
  const { t } = useTranslation('admin');
  const explore = useExplore(source);

  if (explore.isPending) {
    return (
      <div className={styles['state']}>
        <Spinner />
      </div>
    );
  }
  // A failed "load more" keeps the loaded pages; only a failed first page replaces them.
  if (explore.data === undefined) {
    return (
      <div className={styles['state']}>
        <ErrorText error={explore.error} />
        <Button variant="secondary" onClick={() => void explore.refetch()}>
          {t('explore.retry')}
        </Button>
      </div>
    );
  }

  const pages = explore.data.pages;
  const cards = pages.flatMap((page) => page.items);
  const failed = [...new Set(pages.flatMap((page) => page.failedSources))];

  return (
    <>
      {failed.length > 0 && (
        <p role="status" className={styles['notice']}>
          {t('explore.partial', { stores: failed.map((store) => STORE_NAMES[store]).join(', ') })}
        </p>
      )}
      {cards.length === 0 ? (
        <p className={styles['state']}>{t('explore.empty')}</p>
      ) : (
        <ul className={styles['grid']}>
          {cards.map((card) => (
            <li key={cardKey(card)}>
              <ExploreCardView
                card={card}
                gameId={imported[cardKey(card)] ?? card.inCatalog?.gameId ?? null}
                onImported={onImported}
              />
            </li>
          ))}
        </ul>
      )}
      {explore.isFetchNextPageError && <ErrorText error={explore.error} />}
      {explore.hasNextPage && (
        <div className={styles['more']}>
          <Button
            variant="secondary"
            disabled={explore.isFetchingNextPage}
            onClick={() => void explore.fetchNextPage()}
          >
            {explore.isFetchingNextPage ? t('explore.loadingMore') : t('explore.loadMore')}
          </Button>
        </div>
      )}
    </>
  );
}

function ExploreCardView({
  card,
  gameId,
  onImported,
}: {
  card: ExploreCard;
  gameId: string | null;
  onImported: (card: ExploreCard, gameId: string) => void;
}) {
  const { t } = useTranslation('admin');
  const importGame = useExploreImport();
  const meta = [card.year, card.genres.slice(0, 2).join(', ')].filter(Boolean).join(' · ');

  const start = () => {
    importGame.mutate(
      { source: card.source, storeId: card.storeId },
      {
        onSuccess: (result) => onImported(card, result.gameId),
        onError: (error) => {
          // Someone (or another tab) added it meanwhile: the conflict names the catalog game.
          if (error instanceof HttpError && error.body.gameId !== undefined) {
            onImported(card, error.body.gameId);
          }
        },
      },
    );
  };

  return (
    <article className={styles['card']} aria-label={card.title}>
      <CoverTile
        title={card.title}
        coverUrl={card.coverUrl}
        badge={<Badge>{STORE_NAMES[card.source]}</Badge>}
      />
      <div className={styles['caption']}>
        <h3 className={styles['title']} title={card.title}>
          {card.title}
        </h3>
        {meta !== '' && <p className={styles['meta']}>{meta}</p>}
      </div>
      <div className={styles['action']}>
        {gameId !== null ? (
          <div className={styles['inCatalog']}>
            <Badge tone="accent">{t('explore.inCatalog')}</Badge>
            <Link to={`/admin/games/${encodeURIComponent(gameId)}`} className={styles['gameLink']}>
              {t('explore.openGame')}
            </Link>
          </div>
        ) : (
          <Button
            size="sm"
            className={styles['importButton']}
            disabled={importGame.isPending}
            aria-label={
              importGame.isPending ? undefined : t('explore.importTitle', { title: card.title })
            }
            onClick={start}
          >
            {importGame.isPending ? (
              <span className={styles['busy']}>
                <Spinner size={14} />
                {t('explore.importing')}
              </span>
            ) : (
              t('explore.import')
            )}
          </Button>
        )}
        {gameId === null && <ErrorText error={importGame.error} />}
      </div>
    </article>
  );
}
